/**
 * REPORTS PAGE
 * Financial reports and analytics with charts
 * Apple Liquid Glass Design System - WWDC25
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  FileText,
  Download,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  BarChart3,
  PieChart,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { apiClient } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { ProgressBar } from '../../components/ui/ProgressBar'
import { SegmentedControl } from '../../components/ui/SegmentedControl'
import { Chart } from '../../components/ui/Chart'
import { Skeleton } from '../../components/ui/Skeleton'

// Animation configurations
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

export function ReportsPage() {
  const [period, setPeriod] = useState('6months')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ totalRevenue: 0, totalPartners: 0, totalClients: 0, monthlyGrowth: 0 })
  const [monthlyRevenue, setMonthlyRevenue] = useState<Array<{ label: string; value: number; color: string }>>([])
  const [topPartners, setTopPartners] = useState<Array<{ name: string; total_revenue: number; client_count: number }>>([])
  const [topClients, setTopClients] = useState<Array<{ name: string; revenue: number; growth: number }>>([])

  const periodOptions = [
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' },
    { value: '6months', label: '6 Months' },
    { value: '1year', label: '1 Year' }
  ]

  useEffect(() => {
    fetchReportsData()
  }, [period])

  const fetchReportsData = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/dashboard/stats')

      if (response.success) {
        setStats(response.data.stats)

        // Transform monthly revenue data
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const monthlyData = response.data.monthlyRevenue || []

        if (monthlyData.length > 0) {
          setMonthlyRevenue(monthlyData.map((item: any) => {
            const monthIndex = parseInt(item.month.split('-')[1]) - 1
            return {
              label: months[monthIndex],
              value: item.total,
              color: 'from-blue to-blue/80'
            }
          }))
        }

        // Transform top partners data
        if (response.data.topPartners) {
          setTopPartners(response.data.topPartners.map((p: any) => ({
            name: p.name,
            total_revenue: p.total_revenue || 0,
            client_count: p.client_count || 0
          })))
        }
      }
    } catch (error) {
      console.error('Failed to fetch reports data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-8 space-y-8"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
            Reports & Analytics
          </h1>
          <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
            Comprehensive financial insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SegmentedControl
            options={periodOptions}
            value={period}
            onChange={setPeriod}
          />
          <Button>
            <Download className="w-5 h-5 mr-2" />
            Export Report
          </Button>
        </div>
      </motion.div>

      {/* Stats Overview */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            icon={DollarSign}
            color="green"
            trend={stats.monthlyGrowth ? { value: stats.monthlyGrowth, label: 'vs last month' } : undefined}
            subtitle="vs last month"
          />
          <StatCard
            title="Active Partners"
            value={stats.totalPartners}
            icon={Users}
            color="blue"
            badge={<Badge variant="blue" size="sm">Stable</Badge>}
          />
          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={Users}
            color="purple"
            subtitle="Active accounts"
          />
          <StatCard
            title="Avg. Revenue/Partner"
            value={stats.totalPartners > 0 ? formatCurrency(stats.totalRevenue / stats.totalPartners) : '$0'}
            icon={TrendingUp}
            color="orange"
            subtitle="Per partner"
          />
        </StatCardGrid>
      </motion.div>

      {/* Charts Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue" />
              </div>
              <div>
                <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Revenue Trend
                </h3>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Last 6 months
                </p>
              </div>
            </div>
            <Badge variant="green">
              <ArrowUpRight className="w-3 h-3 mr-1" />
              +23.5%
            </Badge>
          </div>
          <Chart
            data={monthlyRevenue}
            height={250}
          />
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple/10 flex items-center justify-center">
                <PieChart className="w-5 h-5 text-purple" />
              </div>
              <div>
                <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Partner Performance
                </h3>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Revenue by partner
                </p>
              </div>
            </div>
          </div>
          <Chart
            data={topPartners.map(p => ({
              label: p.name.split(' ')[0],
              value: p.total_revenue,
              color: 'from-purple to-purple/80'
            }))}
            height={250}
          />
        </GlassCard>
      </motion.div>

      {/* Top Clients Table */}
      <motion.div variants={itemVariants}>
        <ContentCard>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-green" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Top Clients by Revenue
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Ranked by total contribution
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-black/[0.06] dark:border-white/[0.06]">
                  <th className="text-left py-3 px-4 text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">
                    Rank
                  </th>
                  <th className="text-left py-3 px-4 text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">
                    Client
                  </th>
                  <th className="text-right py-3 px-4 text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">
                    Revenue
                  </th>
                  <th className="text-right py-3 px-4 text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">
                    Growth
                  </th>
                </tr>
              </thead>
              <tbody>
                {topClients.length > 0 ? (
                  topClients.map((client, index) => (
                    <motion.tr
                      key={index}
                      variants={listItemVariants}
                      whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                      className="border-b border-black/[0.04] dark:border-white/[0.04] last:border-0"
                    >
                      <td className="py-4 px-4">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-footnote ${
                            index === 0 ? 'bg-gradient-to-br from-yellow to-orange text-white' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 text-white' :
                            index === 2 ? 'bg-gradient-to-br from-orange to-red text-white' :
                            'bg-blue/10 text-blue'
                          }`}
                        >
                          {index + 1}
                        </motion.div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                          {client.name}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-body font-bold text-green">
                          ${client.revenue.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Badge
                          variant={client.growth >= 0 ? 'green' : 'red'}
                          size="sm"
                          icon={client.growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        >
                          {client.growth >= 0 ? '+' : ''}{client.growth}%
                        </Badge>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-8 text-center">
                      <p className="text-subheadline text-light-text-tertiary dark:text-dark-text-tertiary">
                        No client data available yet
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ContentCard>
      </motion.div>

      {/* Summary Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 gap-6">
        {/* Revenue Summary */}
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green" />
            </div>
            <div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Revenue Summary
              </h3>
              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                Total financial overview
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-body font-medium text-light-text-secondary dark:text-dark-text-secondary">
              Total Revenue
            </span>
            <span className="text-title3 font-bold text-green">
              {formatCurrency(stats.totalRevenue)}
            </span>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-subheadline text-light-text-tertiary dark:text-dark-text-tertiary">
              Monthly Growth
            </span>
            <Badge variant={stats.monthlyGrowth >= 0 ? 'green' : 'red'} size="sm">
              {stats.monthlyGrowth >= 0 ? '+' : ''}{stats.monthlyGrowth}%
            </Badge>
          </div>
        </GlassCard>

        {/* Key Metrics */}
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple" />
            </div>
            <div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Key Metrics
              </h3>
              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                Performance indicators
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
            >
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Client Retention</p>
                <p className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">94%</p>
              </div>
              <div className="w-12 h-12 bg-green/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green" />
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
            >
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Avg. Revenue per Client</p>
                <p className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">$562</p>
              </div>
              <div className="w-12 h-12 bg-blue/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue" />
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
            >
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Active Client Rate</p>
                <p className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">75%</p>
              </div>
              <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple" />
              </div>
            </motion.div>
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  )
}
